/******************************************************************************
 * statsApi: typed access to the /stats/** endpoints.                         *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * StatsFilters: the query the module sends
  * toQueryString: filters -> query params
  * fetchOverview / fetchChronoDaysScale
*/

/* Imports */
import { api } from "../../../services/api/api";
/***/

/**
 * The filter state every statistics request carries.
 *
 * Dates travel as `yyyy-MM-dd`, matching the backend contract — sending an ISO timestamp would let
 * the browser's timezone shift the window by a day.
 */
export interface StatsFilters {
  from: string;
  to: string;
  granularity?: EasyTech.StatsGranularity;
  compare?: EasyTech.StatsComparison;
}

/**
 * toQueryString: serialise filters, omitting empty values so the server applies its own defaults.
 */
export function toQueryString(filters: StatsFilters): string {
  const params = new URLSearchParams();
  params.set("from", filters.from);
  params.set("to", filters.to);
  if (filters.granularity) params.set("granularity", filters.granularity);
  if (filters.compare) params.set("compare", filters.compare);
  return params.toString();
}

/**
 * fetchOverview: everything the overview tab renders, in one round trip.
 *
 * One request per tab rather than one per card is deliberate — the aggregations run live on the
 * server with no cache, so a dozen card-level calls would mean a dozen concurrent scans of the same
 * date range.
 */
export async function fetchOverview(filters: StatsFilters): Promise<EasyTech.StatsOverview> {
  const response = await api.get(`stats?${toQueryString(filters)}`);
  return response as EasyTech.StatsOverview;
}

/**
 * fetchInterventions: volumes, durations, schedule deviation and dimension breakdowns.
 *
 * @param toleranceMinutes half-width of the on-time window; omit to use the server default
 */
export async function fetchInterventions(
  filters: StatsFilters,
  toleranceMinutes?: number
): Promise<EasyTech.InterventionStats> {
  const query = toQueryString(filters)
    + (toleranceMinutes ? `&toleranceMinutes=${toleranceMinutes}` : "");
  const response = await api.get(`stats/interventions?${query}`);
  return response as EasyTech.InterventionStats;
}

/**
 * fetchInterventionDelays: lifecycle delays from the work-order audit trail.
 *
 * Kept apart from the volume figures because the trail only starts at migration V44 — this block
 * degrades on its own rather than caveating the whole tab.
 */
export async function fetchInterventionDelays(
  filters: StatsFilters
): Promise<EasyTech.InterventionDelays> {
  const response = await api.get(`stats/interventions/delays?${toQueryString(filters)}`);
  return response as EasyTech.InterventionDelays;
}

/** fetchInterventionMaterials: hoses fitted and the most consumed parts. */
export async function fetchInterventionMaterials(
  filters: StatsFilters
): Promise<EasyTech.InterventionMaterials> {
  const response = await api.get(`stats/interventions/materials?${toQueryString(filters)}`);
  return response as EasyTech.InterventionMaterials;
}

/**
 * fetchChronoDaysScale: the points scale, so the UI can explain the chronodays figure.
 *
 * Configured per deployment through environment variables, hence an endpoint rather than a
 * hard-coded legend.
 */
export async function fetchChronoDaysScale(): Promise<EasyTech.ChronoDaysScale> {
  const response = await api.get("stats/config/chronodays");
  return response as EasyTech.ChronoDaysScale;
}

/**
 * fetchTechnicians: one row per technician active or absent during the period.
 *
 * The server assembles this from a handful of grouped queries merged in memory, so the call costs
 * the same whatever the headcount.
 */
export async function fetchTechnicians(filters: StatsFilters): Promise<EasyTech.TechnicianStats> {
  const response = await api.get(`stats/technicians?${toQueryString(filters)}`);
  return response as EasyTech.TechnicianStats;
}

/** fetchCommercial: visits, their conversion, and the customer base. */
export async function fetchCommercial(filters: StatsFilters): Promise<EasyTech.CommercialStats> {
  const response = await api.get(`stats/commercial?${toQueryString(filters)}`);
  return response as EasyTech.CommercialStats;
}

/**
 * fetchRevenue: invoiced revenue. Admin-only server-side — a planner gets a 403, which the caller
 * treats as "hide the block" rather than as an error worth a toast.
 */
export async function fetchRevenue(filters: StatsFilters): Promise<EasyTech.RevenueStats> {
  const response = await api.get(`stats/revenue?${toQueryString(filters)}`);
  return response as EasyTech.RevenueStats;
}

/** fetchCompliance: safety diagnostics and fleet compliance. Team leader and above. */
export async function fetchCompliance(filters: StatsFilters): Promise<EasyTech.ComplianceStats> {
  const response = await api.get(`stats/compliance?${toQueryString(filters)}`);
  return response as EasyTech.ComplianceStats;
}
/***/
